/**
 * `skyboxData.ts` — web mirror of Unity's `Models/Data/SkyboxData.cs` and
 * the `SkyboxMode` enum (`Models/SceneModel/SkyboxModel.cs`).
 *
 * Colors are Unity-`Color`-style floats (0..1 per channel). The Dynamo wire
 * shape stores them as `M` maps of `r/g/b/a` numbers (see
 * `DynamoClient.UpdateSceneSkybox`, `DynamoClient.cs:2408`); mapping to and
 * from that shape lives in `sceneDto.ts`.
 */

export const SKYBOX_MODE = Object.freeze({
  None: 0,
  Solid: 1,
  Custom: 2,
} as const);
export type SkyboxMode = (typeof SKYBOX_MODE)[keyof typeof SKYBOX_MODE];

/** Unity `SkyboxModel.NoneIdentifier` — texture explicitly cleared. */
export const SKYBOX_NONE_TEXTURE_ID = "none";

/** Unity `SkyboxData.PngFormat` / `JpgFormat` / `HdrFormat`. */
export const SKYBOX_PNG_FORMAT = ".png";
export const SKYBOX_JPG_FORMAT = ".jpg";
export const SKYBOX_HDR_FORMAT = ".hdr";

export interface RgbaColor {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number;
}

/** Unity `SkyboxData.DefaultSolidSkyboxColor` — `#1D1E1F` (29,30,31,255). */
export const DEFAULT_SOLID_SKYBOX_COLOR: RgbaColor = Object.freeze({
  r: 29 / 255,
  g: 30 / 255,
  b: 31 / 255,
  a: 1,
});

/** Unity `SkyboxData.DefaultCustomSkyboxColor` — `#888888` (136,136,136,255). */
export const DEFAULT_CUSTOM_SKYBOX_COLOR: RgbaColor = Object.freeze({
  r: 136 / 255,
  g: 136 / 255,
  b: 136 / 255,
  a: 1,
});

export interface SkyboxData {
  readonly mode: SkyboxMode;
  readonly solidColor: RgbaColor;
  readonly customColor: RgbaColor;
  /** Degrees around the vertical axis (Unity `_Rotation`). */
  readonly rotation: number;
  readonly intensity: number;
  /** Equirect UV tiling (Unity `_Scale`); `1,1` = no tiling. */
  readonly scale: { readonly x: number; readonly y: number };
  /** S3 texture id; empty = legacy (falls back to scene id), `"none"` = cleared. */
  readonly textureId: string;
  /** File extension of the uploaded texture (`".png"` / `".jpg"` / `".hdr"`). */
  readonly format: string;
  /** ISO-8601; Unity `SkyboxData.ModifiedTimestamp` (Dynamo `SkyboxTimestamp`). */
  readonly modifiedAt: string;
}

/** Mirrors the `SkyboxData()` constructor defaults. */
export function createDefaultSkyboxData(): SkyboxData {
  return {
    mode: SKYBOX_MODE.None,
    solidColor: DEFAULT_SOLID_SKYBOX_COLOR,
    customColor: DEFAULT_CUSTOM_SKYBOX_COLOR,
    rotation: 0,
    intensity: 1,
    scale: { x: 1, y: 1 },
    textureId: "",
    format: SKYBOX_PNG_FORMAT,
    modifiedAt: new Date().toISOString(),
  };
}

export function isZeroColor(color: RgbaColor): boolean {
  return color.r === 0 && color.g === 0 && color.b === 0 && color.a === 0;
}

/** `#RRGGBB` for the DOM color affordances (alpha handled separately). */
export function rgbaToHex(color: RgbaColor): string {
  const to255 = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v)) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${to255(color.r)}${to255(color.g)}${to255(color.b)}`;
}

export function hexToRgba(hex: string, alpha = 1): RgbaColor {
  const normalized = hex.replace(/^#/, "");
  const value = Number.parseInt(normalized, 16);
  if (Number.isNaN(value) || (normalized.length !== 6 && normalized.length !== 8)) {
    return { r: 0, g: 0, b: 0, a: alpha };
  }
  if (normalized.length === 8) {
    return {
      r: ((value >>> 24) & 0xff) / 255,
      g: ((value >>> 16) & 0xff) / 255,
      b: ((value >>> 8) & 0xff) / 255,
      a: (value & 0xff) / 255,
    };
  }
  return {
    r: ((value >>> 16) & 0xff) / 255,
    g: ((value >>> 8) & 0xff) / 255,
    b: (value & 0xff) / 255,
    a: alpha,
  };
}

/** The color the given mode renders with (Unity `ApplySkybox` switch). */
export function skyboxColorForMode(skybox: SkyboxData): RgbaColor {
  return skybox.mode === SKYBOX_MODE.Custom ? skybox.customColor : skybox.solidColor;
}
